import type { ComponentType, ReactNode } from 'react'
import { FileText, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  AgendaIcon,
  ContractsIcon,
  InvoicesIcon,
  PaymentsIcon,
  SettingsIcon,
  TasksIcon,
  TechniciansIcon,
} from '@/components/icons/sidebar'

type TopbarIconProps = { className?: string }

function TopbarSvg({
  className,
  width,
  height,
  viewBox,
  children,
}: TopbarIconProps & {
  width: number
  height: number
  viewBox: string
  children: ReactNode
}) {
  return (
    <svg
      width={width}
      height={height}
      viewBox={viewBox}
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={cn('shrink-0', className)}
      aria-hidden
    >
      {children}
    </svg>
  )
}

/** Dashboard grid — Figma TopNavi badge (863:37091). */
export function TopbarGridIcon({ className }: TopbarIconProps) {
  return (
    <TopbarSvg
      className={className}
      width={14}
      height={14}
      viewBox='0 0 12.1 12.1'
    >
      <path
        d='M4.88333 0.8H0.8V4.88333H4.88333V0.8Z'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M11.3 0.8H7.21667V4.88333H11.3V0.8Z'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M11.3 7.21667H7.21667V11.3H11.3V7.21667Z'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M4.88333 7.21667H0.8V11.3H4.88333V7.21667Z'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </TopbarSvg>
  )
}

/** Search magnifier — Figma TopNavi search field (863:37091). */
export function TopbarSearchIcon({ className }: TopbarIconProps) {
  return (
    <TopbarSvg
      className={className}
      width={16}
      height={16}
      viewBox='0 0 13.6 13.6'
    >
      <path
        d='M12.8 12.8L9.9 9.9M11.4667 6.13333C11.4667 9.07885 9.07885 11.4667 6.13333 11.4667C3.18781 11.4667 0.8 9.07885 0.8 6.13333C0.8 3.18781 3.18781 0.8 6.13333 0.8C9.07885 0.8 11.4667 3.18781 11.4667 6.13333Z'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </TopbarSvg>
  )
}

/** Calendar — Figma TopNavi month picker trigger (863:37091). */
export function TopbarCalendarIcon({ className }: TopbarIconProps) {
  return (
    <TopbarSvg
      className={className}
      width={16}
      height={16}
      viewBox='0 0 13.6 14.933'
    >
      <path
        d='M9.46667 0.8V3.46667M4.13333 0.8V3.46667M0.8 6.13333H12.8M2.13333 2.13333H11.4667C12.203 2.13333 12.8 2.73029 12.8 3.46667V12.8C12.8 13.5364 12.203 14.1333 11.4667 14.1333H2.13333C1.39695 14.1333 0.8 13.5364 0.8 12.8V3.46667C0.8 2.73029 1.39695 2.13333 2.13333 2.13333Z'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </TopbarSvg>
  )
}

/** Notification bell — Figma TopNavi (863:37091). */
export function TopbarBellIcon({ className }: TopbarIconProps) {
  return (
    <TopbarSvg
      className={className}
      width={20}
      height={20}
      viewBox='0 0 17 18.664'
    >
      <path
        d='M9.94174 16.8333C9.79523 17.0859 9.58495 17.2955 9.33193 17.4413C9.07892 17.587 8.79206 17.6637 8.50008 17.6637C8.20809 17.6637 7.92124 17.587 7.66822 17.4413C7.41521 17.2955 7.20492 17.0859 7.05841 16.8333M13.5001 6C13.5001 4.67392 12.9733 3.40215 12.0356 2.46447C11.0979 1.52678 9.82616 1 8.50008 1C7.174 1 5.90223 1.52678 4.96454 2.46447C4.02686 3.40215 3.50008 4.67392 3.50008 6C3.50008 11.8333 1.00008 13.5 1.00008 13.5H16.0001C16.0001 13.5 13.5001 11.8333 13.5001 6Z'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </TopbarSvg>
  )
}

/** Users — Figma Clients TopNavi badge (2853:49768). */
export function TopbarUsersIcon({ className }: TopbarIconProps) {
  return (
    <TopbarSvg
      className={className}
      width={14}
      height={12}
      viewBox='0 0 14.4333 12.1'
    >
      <path
        d='M10.1333 11.3V10.1333C10.1333 9.51449 9.8875 8.921 9.44992 8.48342C9.01233 8.04583 8.41884 7.8 7.8 7.8H3.13333C2.51449 7.8 1.921 8.04583 1.48342 8.48342C1.04583 8.921 0.8 9.51449 0.8 10.1333V11.3M13.6333 11.3V10.1333C13.6329 9.61634 13.4609 9.11412 13.1441 8.70552C12.8274 8.29692 12.3839 8.00508 11.8833 7.87583M9.55 0.875833C10.0519 1.00434 10.4968 1.29624 10.8145 1.70551C11.1321 2.11479 11.3046 2.61815 11.3046 3.13625C11.3046 3.65435 11.1321 4.15771 10.8145 4.56699C10.4968 4.97626 10.0519 5.26816 9.55 5.39667M7.8 3.13333C7.8 4.422 6.75533 5.46667 5.46667 5.46667C4.178 5.46667 3.13333 4.422 3.13333 3.13333C3.13333 1.84467 4.178 0.8 5.46667 0.8C6.75533 0.8 7.8 1.84467 7.8 3.13333Z'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </TopbarSvg>
  )
}

/** Module → icon for the TopBar breadcrumb badge. */
const MODULE_ICONS: Record<string, ComponentType<TopbarIconProps>> = {
  dashboard: TopbarGridIcon,
  calendar: AgendaIcon,
  jobs: AgendaIcon,
  clients: TopbarUsersIcon,
  quotes: FileText,
  invoices: InvoicesIcon,
  'credit-notes': InvoicesIcon,
  payments: PaymentsIcon,
  contracts: ContractsIcon,
  tasks: TasksIcon,
  technicians: TechniciansIcon,
  settings: SettingsIcon,
  notifications: TopbarBellIcon,
  'my-account': User,
}

export function getTopbarModuleIcon(
  segment: string | undefined
): ComponentType<TopbarIconProps> {
  if (!segment) return TopbarGridIcon
  return MODULE_ICONS[segment] ?? TopbarGridIcon
}
